import { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { BoxArrowLeft, ChevronDown, ChevronRight, List, Person } from 'react-bootstrap-icons';
import Form from 'react-bootstrap/Form';
import { NavLink } from 'react-router-dom';
import { getFromSessionStorage, setInSessionStorage } from '../../constants/functions';
import { useNavbarContext } from '../../context/NavbarContext';
import { useUserContext } from '../../context/UserContext';
import { useGetWindowSize } from '../../hooks/useGetWindowSize';
import { useStylization } from '../../hooks/useStylization';
import PageList from '../PageList';
import { SmallInfo } from '../Sidenav/styles';
import {
  ButtonCollapse,
  CanvasBody,
  CanvasContainer,
  FalseButton,
  GroupQuotaContainer,
  HeaderContainer,
  HeaderPageContainer,
  LogoContainer,
  LogoSidenav,
  UserContainer,
} from './styles';

const Header = () => {
  const [isShow, setIsShow] = useState(false);
  const collapseButtonRef = useRef<HTMLButtonElement | null>(null);
  const { setOpenMenu } = useNavbarContext();

  const handleShow = useCallback(() => {
    setIsShow((prev) => !prev);
  }, []);

  useEffect(() => {
    if (getFromSessionStorage('minimize')) {
      setTimeout(() => setIsShow(true), 1);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      collapseButtonRef.current?.click();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const { loginResponseData, setGroup, group, makeLogout, setQuota, quota, setDigit, digit } = useUserContext();
  const { stylization } = useStylization();
  const { isMobile } = useGetWindowSize();

  useEffect(() => {
    setOpenMenu(!isShow);
  }, [isShow, setOpenMenu]);

  useEffect(() => {
    if (!isMobile) setIsShow(false);
  }, [isMobile]);

  const handleLoggout = () => {
    makeLogout();
  };

  const style = {
    transform: isShow ? 'rotate(180deg)' : '',
    transition: 'transform 150ms ease',
  };

  useEffect(() => {
    const savedGroup = getFromSessionStorage('group');
    const savedQuota = getFromSessionStorage('quota');
    const savedDigit = getFromSessionStorage('digit');

    if (savedGroup && savedQuota && savedDigit) {
      setGroup(savedGroup);
      setQuota(savedQuota);
      setDigit(savedDigit);
    }
  }, []);

  useEffect(() => {
    const storedGroup = getFromSessionStorage('group');
    const storedQuota = getFromSessionStorage('quota');
    const storedDigit = getFromSessionStorage('digit');

    if (loginResponseData?.consorciados && storedGroup && storedQuota && storedDigit) {
      const selectedConsorciado = loginResponseData.consorciados.find(
        (consorciado) =>
          parseInt(consorciado.grupo) === parseInt(storedGroup) &&
          parseInt(consorciado.cota) === parseInt(storedQuota) &&
          consorciado.digito === storedDigit
      );

      if (selectedConsorciado) {
        // mantém os valores crus (sem zeros à esquerda) no contexto, pois é o que
        // as APIs de extrato/consolidada esperam. O padding é só para exibição.
        setGroup(selectedConsorciado.grupo);
        setQuota(selectedConsorciado.cota);
        setDigit(selectedConsorciado.digito);
      }
    }
  }, [loginResponseData]);

  return (
    <HeaderContainer>
      <div className="d-flex align-items-center justify-content-between">
        <ButtonCollapse ref={collapseButtonRef} onClick={handleShow} type="button">
          {isMobile ? <List size={22} /> : <ChevronRight size={14} fontWeight={700} color="black" style={style} />}
        </ButtonCollapse>

        <CanvasContainer show={isShow} onHide={handleShow} scroll={false} backdrop={false} className="d-md-none">
          <CanvasBody>
            <PageList minimize={false} hideLabel={false} onClick={() => handleShow()} />
          </CanvasBody>
        </CanvasContainer>

        <LogoContainer>
          <NavLink to="/">
            <LogoSidenav src={stylization?.logoSidebar} />
          </NavLink>
        </LogoContainer>
        <FalseButton />
      </div>

      <HeaderPageContainer>
        <GroupQuotaContainer>
          <p>Grupo / Quota</p>
          <div className="position-relative d-flex align-items-center">
            <Form.Select
              aria-label="Selecione o Grupo e Cota"
              value={group && quota && digit ? `${group.padStart(5, '0')}/${quota.padStart(4, '0')}.${digit}` : ''}
              id="select"
              onChange={(e) => {
                const [groupValue, quotaWithDigit] = e.target.value.split('/');
                const [quotaValue, digitValue] = quotaWithDigit.split('.');

                const selected = loginResponseData?.consorciados.find(
                  (cons) =>
                    parseInt(cons.grupo) === parseInt(groupValue) &&
                    parseInt(cons.cota) === parseInt(quotaValue) &&
                    cons.digito === digitValue
                );

                if (selected) {
                  // grava sempre os valores crus do consorciado (sem zeros à esquerda),
                  // que é o formato aceito pelas APIs.
                  setInSessionStorage('cpfCnpj', selected.cpfCnpj);
                  setGroup(selected.grupo);
                  setQuota(selected.cota);
                  setDigit(selected.digito);
                  setInSessionStorage('group', selected.grupo);
                  setInSessionStorage('quota', selected.cota);
                  setInSessionStorage('digit', selected.digito);
                } else {
                  console.warn(' Cota selecionada não encontrada:', e.target.value);
                }
              }}
            >
              {loginResponseData?.consorciados.map((consorciado, index) => {
                const formattedValue = `${consorciado.grupo.padStart(5, '0')}/${consorciado.cota.padStart(4, '0')}.${
                  consorciado.digito
                }`;

                return (
                  <option
                    key={`${consorciado.grupo}_${consorciado.cota}_${consorciado.digito}_${index}`}
                    value={formattedValue}
                  >
                    {consorciado.grupo} / {consorciado.cota.padStart(4, '0')}.{consorciado.digito}
                  </option>
                );
              })}
            </Form.Select>

            <div className="icon" style={{ marginLeft: '-30px' }}>
              <ChevronDown size={14} fontWeight={700} color="black" />
            </div>
          </div>
        </GroupQuotaContainer>

        <UserContainer>
          <Dropdown align={{ lg: 'end' }}>
            <Dropdown.Toggle className="p-0">
              <Person size={22} />
              <span className="d-none d-md-flex">{loginResponseData?.usuarioApelido}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item href="#/action-2" style={{ color: 'red' }} onClick={() => handleLoggout()}>
                <BoxArrowLeft size={20} /> Sair
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </UserContainer>
      </HeaderPageContainer>
    </HeaderContainer>
  );
};

export default Header;
